/**
 * Learned preference signal.
 *
 * These are *hints*, never rules. They ride along with the branch request so the model
 * can lean toward the kinds of movement this player actually takes, but nothing here is
 * allowed to filter suggestions out — the point of the product is to be surprised, and a
 * preference model that narrows the graph defeats it.
 */

// From `constants.js`, not `schemas.js`: this module is on the client's startup path and
// must not drag Zod in behind one frozen array.
import { MOVEMENT_KINDS, type MovementKind } from '../llm/constants.js';
import type { PitchClass } from '../chord/types.js';

export interface PreferenceHints {
  /** How often each kind of movement has been chosen by playing it. */
  takenKindCounts: Record<MovementKind, number>;
  /** Exponential moving average of the adventurousness the player settles on. */
  adventurousnessEma: number;
  /** Tonics that recur across sessions, most frequent first. */
  commonKeys: PitchClass[];
  /** 0..1 — how often an explanation is opened, a proxy for wanting the theory. */
  explanationTapRate: number;
  /** Transitions the player explicitly marked as liked. */
  likedTransitions: Array<{ fromPcs: PitchClass[]; toPcs: PitchClass[] }>;
}

/** A zeroed set of hints, for a first session or a reset. */
export function createEmptyPreferenceHints(): PreferenceHints {
  const takenKindCounts = {} as Record<MovementKind, number>;
  for (const kind of MOVEMENT_KINDS) takenKindCounts[kind] = 0;
  return {
    takenKindCounts,
    adventurousnessEma: 0.5,
    commonKeys: [],
    explanationTapRate: 0,
    likedTransitions: [],
  };
}

/** Smoothing factor for `adventurousnessEma`; roughly a ten-observation memory. */
export const ADVENTUROUSNESS_EMA_ALPHA = 0.2;

/** Fold one observation into a moving average. Pure, so the store can call it freely. */
export function updateEma(previous: number, observation: number, alpha = 0.2): number {
  return previous + alpha * (observation - previous);
}
